import PropTypes from "prop-types";
import React from "react";
import ENV from "app/utils/ENV";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  Animated,
  ActivityIndicator,
} from "react-native";
import { styleSheet } from "app/utils/styleSheet";
import Text from "app/components/Text";
import valueHelpers from "app/utils/valueHelpers";
import textHelpers from "app/utils/textHelpers";
import colorHelpers from "app/utils/colorHelpers";
import valueActions from "app/utils/valueActions";
import * as RootNavigation from "app/utils/RootNavigation.js";
import store from "app/redux/store";

import MomentList from "app/components/chapter/MomentList";
import Icon from "app/components/Icon";

import Svg, { Path, Ellipse, Circle, Rect } from "react-native-svg";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import dayjs from "dayjs";

import { StyleContext } from "app/contexts/Style";

export default class AreaGraph extends React.Component {
  static contextType = StyleContext;

  constructor(props, context) {
    super(props, context);
    this.state = {
      loading: true,
      hiddenIds: [],
      tooltipData: null,
      tooltipY: null,
      tooltipX: null,
    };
  }

  componentDidMount = async () => {};

  render() {
    let { hiddenIds, tooltipData, tooltipY, tooltipX } = this.state;
    let { data, startAt, endAt, chapters, focusedValueId } = this.props;
    // if (!moments.length && !data) return null;

    let moments = data
      .map((d) => [
        ...d.moments.map((m) => ({ ...m, attribution_id: d.value.id })),
      ])
      .flat()
      .map((m) => {
        let timestamp = m.timestamp || m.completed_at || m.updated_at;
        let chapterStartAt = chapters?.find(
          (c) => c.id === m.chapter_id,
        )?.start_at;
        if (!!chapterStartAt && chapterStartAt > timestamp) {
          timestamp = chapterStartAt;
        }
        return {
          ...m,
          timestamp,
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    // if (!!endAt) {
    //   moments = moments.filter((m) => {
    //     return new Date(m.timestamp).getTime() <= new Date(endAt).getTime();
    //   });
    // }
    const timestamps = moments?.map((m) => m.timestamp).sort();

    const valueIds = textHelpers.uniq(data.map((d) => d.value.id));

    let minDate, maxDate;
    let dayChunks = 1;

    let range = [];
    if (!!timestamps[0]) {
      range =
        !!startAt && !!endAt
          ? [
              Math.floor(new Date(startAt).getTime() / 86400000) * 86400000,
              Math.ceil(new Date(endAt).getTime() / 86400000) * 86400000,
            ]
          : [
              Math.floor(new Date(timestamps[0]).getTime() / 86400000) *
                86400000,
              Math.ceil(
                new Date(timestamps[timestamps.length - 1]).getTime() /
                  86400000,
              ) * 86400000,
            ];
      const rangeInDays = Math.ceil((range[1] - range[0]) / 86400000);
      minDate = new Date(range[0] - rangeInDays * 0.5 * 86400000);
      // maxDate = new Date(range[1] + rangeInDays * 0.5 * 86400000);
      maxDate = new Date(range[1]);

      // dayChunks = Math.round(rangeInDays / 7);
      dayChunks = Math.round(rangeInDays / 12);
    }

    let chartData = this.generateFrequencyBins(moments, dayChunks); // daily bins
    let dailyData = this.generateFrequencyBins(moments, 1);

    if (!chartData.length) return null;
    // bins = this.applyMovingAverage(bins, 7); // 5-day rolling average
    // bins = bins.filter((_, i) => i % 3 === 0);
    // const bins = this.generateFrequencyBins(timestamps);

    const startYear = dayjs(minDate).year();
    const endYear = dayjs(maxDate).year();
    let secondaryTicks = [];
    for (let y = startYear; y <= endYear; y++) {
      secondaryTicks.push(dayjs(`${y}-01-01`).valueOf());
    }
    // add today as well
    secondaryTicks.push(dayjs().startOf("day").valueOf());
    // console.log(secondaryTicks);

    return (
      <View>
        <Text>{`${dayjs(range[0]).format("M/D/YY")} – ${dayjs(range[1]).format("M/D/YY")}`}</Text>
        <Text>
          {data.length} values with{" "}
          {data.reduce((acc, d) => {
            return acc + d.moments.length;
          }, 0)}{" "}
          moments{`\n\n`}
        </Text>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={chartData}
            // onMouseMove={(e) => {
            //   this.setState({ tooltipX: e.chartX, tooltipY: e.chartY });
            //   console.log('payload', e?.activePayload);
            //   if (e?.activeLabel) {
            //     const matchingDay = dailyData.find(
            //       (d) => d.time === e.activeLabel,
            //     );
            //     console.log("matchingDay", matchingDay);
            //     if (matchingDay) this.setState({ tooltipData: matchingDay });
            //   }
            // }}
            // onMouseLeave={() => this.setState({ tooltipData: null })}
          >
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={[
                dayjs().subtract(1, "month").valueOf(),
                dayjs().valueOf(),
              ]}
              xAxisId="primary"
              orientation="bottom"
              // ticks={ticks}
              tickFormatter={(t) => {
                return dayjs(t).format("M/D/YY");
              }}
              tick={{ fill: this.context.colors.foreground, fontSize: 15 }}
              tickSize={16}
              allowDataOverflow={true}
              domain={[startAt || "dataMin", endAt || "dataMax"]}
            />
            <YAxis />
            {this.renderReferenceLines(range, chartData.length)}
            {data.map((d) => {
              let value = d.value;
              let valueColor = valueHelpers.getColor(
                value,
                this.context.colorScheme,
              );
              let strokeOpacity = 0.9;
              let fillOpacity = 0.5;
              if (!!focusedValueId) {
                if (focusedValueId != value.id) {
                  fillOpacity = 0.1;
                  strokeOpacity = 0.2;
                }
              }
              return (
                <Area
                  key={value.id}
                  // type="monotone"
                  type="basis"
                  dataKey={`${value.id}`}
                  name={value.title}
                  stroke={colorHelpers.hexToRgbaString(
                    valueColor,
                    strokeOpacity,
                  )}
                  fill={colorHelpers.hexToRgbaString(valueColor, fillOpacity)}
                  xAxisId="primary"
                  // onClick={(data, index) => {
                  //   console.log(`Clicked: ${value.title}`, data, index, value);
                  //   // Trigger your custom logic here
                  // }}
                  dot={false}
                  // activeDot={false}
                />
              );
            })}
            <Tooltip
              labelFormatter={(t) => {
                let f = "M/D/YY";
                return dayjs(t).format(f);
              }}
              // itemSorter={(a) => -a.payload[a.dataKey]}
              contentStyle={{
                borderWidth: 1,
                borderColor: this.context.colors.trim,
                borderRadius: 4,
                backgroundColor: colorHelpers.hexToRgbaString(
                  this.context.colors.empty,
                  0.6,
                ),
                overflow: "hidden",
                maxWidth: 250,
                marginLeft: 20,
              }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                return (
                  <View
                    style={{
                      padding: 5,
                      borderWidth: 1,
                      borderColor: this.context.colors.trim,
                      borderRadius: 4,
                      backgroundColor: colorHelpers.hexToRgbaString(
                        this.context.colors.empty,
                        0.6,
                      ),
                      overflow: "hidden",
                      maxWidth: 250,
                      zIndex: 3,
                    }}
                  >
                    <Text bold style={{ marginBottom: 4 }}>
                      {dayjs(label).format("M/D/YY")}-
                      {dayjs(label).add(dayChunks, "days").format("M/D/YY")}
                    </Text>

                    {payload
                      .sort((a, b) => b.value - a.value)
                      .map((entry) => {
                        return (
                          <TouchableOpacity
                            key={entry.dataKey}
                            onPress={() => {
                              alert(entry.name);
                              if (hiddenIds.includes(entry.data.dataId)) {
                                // hiddenIds
                              }
                            }}
                          >
                            <Text
                              style={{
                                color: entry.color,
                                marginBottom: 2,
                                opacity: hiddenIds.includes(entry.dataKey)
                                  ? 0.5
                                  : 1,
                              }}
                            >
                              ({entry.value}) {entry.name}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                  </View>
                );
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </View>
    );
  }

  renderReferenceLines = (range, renderIndex) => {
    // return null;
    const rangeInDays = Math.ceil((range[1] - range[0]) / 86400000);
    let params = {
      // isFront: true,
      xAxisId: "primary",
      stroke: this.context.colors.fill,
      // strokeDasharray: "3 3",
      label: {
        position: "bottom",
        fill: this.context.colors.trim,
        fontSize: 12,
      },
    };
    let referenceLines = [
      <ReferenceLine
        key={"today"}
        isFront={true}
        {...params}
        label={{ ...params.label, value: "Today" }}
        x={dayjs().startOf("month").valueOf()}
      />,
    ];

    return referenceLines[0];

    if (rangeInDays < 365) {
      // months
      // add a reference line for each month in the range
      let monthStart = dayjs(range[0]).startOf("year");
      let monthEnd = dayjs(range[1]).endOf("year");
      let month = monthStart;

      while (month.isBefore(monthEnd)) {
        referenceLines.push(
          <ReferenceLine
            key={renderIndex + month.valueOf()}
            {...params}
            stroke={this.context.colors.trim}
            x={month.valueOf()}
            label={{
              ...params.label,
              value: month.format("MMM"),
            }}
          />,
        );
        month = month.add(1, "month");
      }
      console.log(referenceLines);
    } else {
      // year
      // add a reference line for each year in the range
      let yearStart = dayjs(range[0]).startOf("year");
      let yearEnd = dayjs(range[1]).endOf("year");
      let year = yearStart;
      while (year.isBefore(yearEnd)) {
        referenceLines.push(
          <ReferenceLine
            key={renderIndex + year.valueOf()}
            {...params}
            x={year.valueOf()}
            label={{
              ...params.label,
              value: year.format("YYYY"),
            }}
          />,
        );
        year = year.add(1, "year");
      }
    }
    return referenceLines;
  };

  applyMovingAverage = (data, windowSize = 3) => {
    const result = [];

    for (let i = 0; i < data.length; i++) {
      const windowStart = Math.max(0, i - windowSize + 1);
      const window = data.slice(windowStart, i + 1);
      const average =
        window.reduce((sum, d) => sum + d.count, 0) / window.length;
      result.push({
        time: data[i].time,
        count: average,
      });
    }

    return result;
  };

  generateFrequencyBins = (moments, daysChunk = 1) => {
    const intervalMs = 3600_000 * 24 * daysChunk;
    const cushionMs = intervalMs; // one extra chunk before/after

    // Map<bucketTs, Map<string(valueId), number(count)>>
    const counts = new Map();

    // collect every distinct attribution_id (as a string)
    const allIds = new Set();

    // 1) tally counts per bucket per id
    for (const m of moments) {
      const ts = new Date(m.timestamp).getTime();
      const bucket = Math.floor(ts / intervalMs) * intervalMs;
      const id = String(m.attribution_id);

      allIds.add(id);

      if (!counts.has(bucket)) {
        counts.set(bucket, new Map());
      }
      const bucketMap = counts.get(bucket);
      bucketMap.set(id, (bucketMap.get(id) || 0) + 1);
    }

    if (allIds.size === 0) {
      return []; // no data → empty result
    }

    // 2) find time range (with cushion)
    const times = moments.map((m) => new Date(m.timestamp).getTime());
    const minBucket = Math.floor(Math.min(...times) / intervalMs) * intervalMs;
    const maxBucket = Math.ceil(Math.max(...times) / intervalMs) * intervalMs;
    const start = minBucket - cushionMs;
    const end = maxBucket + cushionMs;

    // 3) build result array, filling zeros for any missing id/interval
    const result = [];
    for (let t = start; t <= end; t += intervalMs) {
      const bucketMap = counts.get(t) || new Map();
      const entry = { time: t };

      // for each id, pull its bucket count or zero
      for (const id of allIds) {
        entry[id] = bucketMap.get(id) || 0;
      }

      result.push(entry);
    }

    return result;
  };

  styles = () => {
    return {};
  };
}
